import type { Team } from "../types/database";
import { supabase } from "./supabase";

export type TeamDetailMember = {
  student_id: string;
  full_name: string | null;
  avatar_url: string | null;
  joined_at: string | null;
  is_captain: boolean;
};

export type TeamDetail = {
  team: Team;
  competition: {
    id: string;
    name: string;
    status: "draft" | "open" | "judging" | "closed";
    closes_at: string | null;
    opens_at: string | null;
    max_team_size: number;
    team_size_min: number;
    competition_type: "capped" | "uncapped" | "invite_only";
    company_name: string | null;
  };
  members: TeamDetailMember[];
  /** Original creator. Kept for back-compat; prefer `is_captain` for permissions. */
  is_owner: boolean;
  /** True if the current user is a captain of this team. Multiple captains allowed. */
  is_captain: boolean;
  is_member: boolean;
};

export async function getTeamDetail(teamId: string): Promise<{ detail: TeamDetail | null; error: Error | null }> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) {
    return { detail: null, error: new Error(userErr?.message ?? "Not signed in") };
  }
  const uid = userData.user.id;

  const { data: team, error: teamErr } = await supabase
    .from("teams")
    .select(
      "id, competition_id, name, visibility, owner_id, is_locked, mute_requests, description, looking_for, qualified_at, created_at, updated_at"
    )
    .eq("id", teamId)
    .maybeSingle();

  if (teamErr || !team) {
    return { detail: null, error: new Error(teamErr?.message ?? "Team not found") };
  }

  const teamRow = team as Team;

  const { data: comp, error: compErr } = await supabase
    .from("competitions")
    .select("id, name, status, closes_at, opens_at, max_team_size, team_size_min, competition_type, company_id")
    .eq("id", teamRow.competition_id)
    .maybeSingle();

  if (compErr || !comp) {
    return { detail: null, error: new Error(compErr?.message ?? "Competition not found") };
  }

  let companyName: string | null = null;
  if ((comp as { company_id?: string | null }).company_id) {
    const { data: company } = await supabase
      .from("companies")
      .select("name")
      .eq("id", (comp as { company_id: string }).company_id)
      .maybeSingle();
    companyName = (company as { name?: string | null } | null)?.name ?? null;
  }

  const { data: members } = await supabase
    .from("team_members")
    .select("student_id, joined_at, is_captain")
    .eq("team_id", teamRow.id);

  const studentIds = [...new Set([...(members ?? []).map((m) => m.student_id as string), teamRow.owner_id])];
  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, full_name, avatar_url")
    .in("id", studentIds);

  type ProfileRow = { id: string; full_name: string | null; avatar_url: string | null };
  const byId = new Map<string, ProfileRow>();
  for (const p of profiles ?? []) {
    byId.set(p.id as string, p as ProfileRow);
  }

  const memberRows: TeamDetailMember[] = (members ?? []).map((m) => {
    const p = byId.get(m.student_id as string);
    return {
      student_id: m.student_id as string,
      full_name: p?.full_name ?? null,
      avatar_url: p?.avatar_url ?? null,
      joined_at: (m.joined_at as string | null) ?? null,
      is_captain: Boolean((m as { is_captain?: boolean | null }).is_captain),
    };
  });

  // Ensure owner is in member list (some old teams don't auto-insert owner into team_members).
  if (!memberRows.some((r) => r.student_id === teamRow.owner_id)) {
    const p = byId.get(teamRow.owner_id);
    memberRows.unshift({
      student_id: teamRow.owner_id,
      full_name: p?.full_name ?? null,
      avatar_url: p?.avatar_url ?? null,
      joined_at: null,
      is_captain: true,
    });
  }

  // Sort: captains first, then alphabetical by name.
  memberRows.sort((a, b) => {
    if (a.is_captain && !b.is_captain) return -1;
    if (!a.is_captain && b.is_captain) return 1;
    return (a.full_name ?? "").localeCompare(b.full_name ?? "");
  });

  const is_member = memberRows.some((m) => m.student_id === uid);
  const is_captain = memberRows.some((m) => m.student_id === uid && m.is_captain);

  const compStatus = (comp as { status: string }).status;
  const normalizedStatus =
    compStatus === "open" || compStatus === "judging" || compStatus === "closed" || compStatus === "draft"
      ? compStatus
      : "draft";

  return {
    detail: {
      team: teamRow,
      competition: {
        id: comp.id as string,
        name: (comp as { name: string }).name,
        status: normalizedStatus as "draft" | "open" | "judging" | "closed",
        closes_at: (comp as { closes_at: string | null }).closes_at,
        opens_at: (comp as { opens_at: string | null }).opens_at ?? null,
        max_team_size: ((comp as { max_team_size: number | null }).max_team_size ?? 1) as number,
        team_size_min: ((comp as { team_size_min: number | null }).team_size_min ?? 1) as number,
        competition_type: ((comp as { competition_type?: "capped" | "uncapped" | "invite_only" | null }).competition_type ??
          "capped") as "capped" | "uncapped" | "invite_only",
        company_name: companyName,
      },
      members: memberRows,
      is_owner: teamRow.owner_id === uid,
      is_captain,
      is_member,
    },
    error: null,
  };
}

/**
 * Promote a team member to captain. Caller must already be a captain on the team
 * (enforced by the SECURITY DEFINER RPC).
 */
export async function promoteTeamMemberToCaptain(
  teamId: string,
  studentId: string
): Promise<{ error: Error | null }> {
  const { error } = await supabase.rpc("promote_team_member_to_captain", {
    p_team_id: teamId,
    p_student_id: studentId,
  });
  if (error) return { error: new Error(error.message) };
  return { error: null };
}
